import { CubePresenter, Logger, NOOP_VOID } from 'wdc-cube';
import { reuseRows } from '../../../../../components/rows';
import { hasAnyRole, isValidCpf, maskCpf, maskPhone } from '../../../../../domain';
import { CompanyUsersApi } from '../company-users.api';
import { Places } from '../../../../route-consts';
import { CompanyUsersKeys } from '../company-users.key';
import { ProfessionalTypeChoice } from '../professional-type-choice';
import { CompanyUserDetailKeys } from './company-user-detail.key';
import { AssignRowScope, CompanyUserDetailScope, ProfessionalTypeScope } from './company-user-detail.scope';
const LOG = Logger.get('Mormaii.CompanyUserDetailPresenter');
// @Inject
const api = CompanyUsersApi.INSTANCE;
/**
 * A ficha de um usuario da empresa.
 *
 * ## Por que deixou de ser dois dialogos
 *
 * Antes havia "corrigir CPF" e "atribuir colaboradores", cada um numa porta. Quem
 * abrisse o primeiro via um campo de CPF e concluia, com razao, que era so isso que
 * dava para configurar - e o resto (nome, telefone, registro de conselho) nao tinha
 * tela em lugar nenhum, porque tambem nao tinha endpoint.
 *
 * ## A ordem do salvar nao e detalhe
 *
 * O CPF vai PRIMEIRO, e sozinho: e o unico campo que o servidor pode recusar por
 * conflito ("ja pertence a outro usuario"). Salvando-o por ultimo, uma recusa
 * deixaria o resto ja gravado e a tela mostrando um erro sobre uma edicao que em
 * parte aconteceu.
 */
export class CompanyUserDetailPresenter extends CubePresenter {
    constructor(app) {
        super(app, new CompanyUserDetailScope());
        this.parentSlot = NOOP_VOID;
        this.choice = new ProfessionalTypeChoice();
        this.collaborators = [];
        /**
         * Os colaboradores cuja atribuicao esta indo ao servidor.
         *
         * Guardado aqui, e nao so na linha: a linha e reaproveitada a cada `showRows`
         * (`reuseRows`) e o montador reescreve todos os campos dela - inclusive `busy`, que
         * antes ele nao tocava, e o interruptor ficava travado depois de um sucesso.
         */
        this.toggling = new Set();
    }
    async applyParameters(intent, initialization, last) {
        const keys = new CompanyUserDetailKeys(this.app, intent);
        if (!(await keys.owner?.allowEntry('company-users', intent))) {
            return false;
        }
        // Atributos da intencao: lidos a cada chamada, como os slots. Vem de quem esta
        // acima na composicao, e reler custa nada.
        this.owner = keys.owner;
        this.area = keys.area;
        if (initialization) {
            this.scope.onFirstNameChanged = this.handleChanged.bind(this, 'firstName');
            this.scope.onLastNameChanged = this.handleChanged.bind(this, 'lastName');
            this.scope.onSocialNameChanged = this.handleChanged.bind(this, 'socialName');
            this.scope.onRegistrationChanged = this.handleChanged.bind(this, 'registrationNumber');
            this.scope.onPhoneChanged = this.handlePhoneChanged.bind(this);
            this.scope.onCpfChanged = this.handleCpfChanged.bind(this);
            this.scope.onUfChanged = this.handleUfChanged.bind(this);
            this.scope.onTermChanged = this.handleTermChanged.bind(this);
            this.scope.onClearType = this.action(this.onClearType);
            this.scope.onSave = this.action(this.onSave);
            this.scope.onToggleActive = this.action(this.onToggleActive);
            this.scope.onRemove = this.action(this.onRemove);
            this.scope.onMakeMaster = this.action(this.onMakeMaster);
            this.scope.onBack = this.action(this.onBack);
            LOG.info('Initialized');
        }
        // O slot e decidido acima na composicao e pode mudar entre viagens: le-se sempre.
        this.parentSlot = keys.parentSlot;
        this.userId = keys.target;
        /*
         * A empresa em ESCOPO quando o endereco nao a traz - a mesma regra da lista.
         *
         * Quem administra mais de uma empresa escolhe no CABECALHO, e ai `companyIdInAddress`
         * devolve `undefined` de proposito: a empresa ja viaja em `ce`, e publica-la de novo
         * daria dois donos ao mesmo numero. A lista sempre soube disso e lia
         * `keys.companyId ?? owner.companyInScope`; a ficha lia so a primeira metade.
         *
         * O efeito era este: para quem administra duas empresas, abrir a ficha de alguem
         * mandava toda chamada SEM empresa, e o servidor respondia "Voce administra mais de
         * uma empresa. Informe qual." - um aviso correto para uma pergunta que a tela nao
         * devia estar fazendo.
         */
        this.companyId = keys.companyId ?? keys.owner?.companyInScope;
        if (this.userId === undefined) {
            // Endereco de uma versao anterior, ou de quem ja saiu da empresa. Redireciona
            // com o intent em transito: no meio do flip o `of(app)` perguntaria a quem
            // ainda nao foi comitado, e a empresa do link se perderia.
            await this.app.flipToIntent(intent.redirect(Places.companyUsers));
            return false;
        }
        if (last) {
            this.owner?.showSection('company-users', 'Acesso ao painel', 'Ficha de quem acessa');
        }
        // O slot ANTES da rede: sem isto a tela anterior congela durante a busca.
        this.parentSlot(this.scope);
        await this.load();
        return true;
    }
    publishParameters(intent) {
        const keys = new CompanyUserDetailKeys(this.app, intent);
        keys.target = this.userId;
    }
    async load() {
        this.scope.loading = true;
        this.scope.error = undefined;
        try {
            const user = await api.fetchUser(this.userId, this.companyId);
            this.loaded = user;
            this.show(user);
            // As profissoes e os colaboradores so interessam a quem E profissional,
            // e cada um custa uma consulta.
            if (user.profissional) {
                await Promise.all([this.loadTypes(), this.loadCollaborators(user.professionalId)]);
            }
        }
        catch (caught) {
            LOG.warn('Could not load the company user', caught);
            this.scope.error = messageOf(caught, 'Não foi possível carregar este usuário.');
        }
        finally {
            this.scope.loading = false;
        }
    }
    show(user) {
        this.scope.name = (user.socialName ?? '').trim() || `${user.firstName} ${user.lastName}`.trim();
        this.scope.email = user.email;
        this.scope.active = user.active;
        this.scope.profissional = user.profissional;
        this.scope.papel = [
            user.administrador && (user.master ? 'Administrador master' : 'Administrador'),
            user.profissional && 'Profissional'
        ]
            .filter(Boolean)
            .join(' · ');
        this.scope.master = user.master;
        this.scope.podeAlterarAcesso = user.podeAlterarAcesso;
        this.scope.podeTornarMaster = user.podeTornarMaster;
        this.scope.motivoSemAcesso = this.reasonWithoutAccess(user);
        this.scope.firstName = user.firstName ?? '';
        this.scope.lastName = user.lastName ?? '';
        this.scope.socialName = user.socialName ?? '';
        this.scope.phone = maskPhone(user.telephoneNumber ?? '');
        this.scope.cpf = user.cpf ?? '';
        this.scope.cpfFicticio = user.cpfFicticio;
        this.scope.showRegistration = user.profissional;
        this.scope.showAssignment = user.profissional;
        this.scope.registrationNumber = user.registrationNumber ?? '';
        this.scope.uf = user.uf ?? '';
        this.choice.restore(user.professionalTypeId);
        this.showTypes();
        this.scope.dirty = false;
        this.scope.formError = undefined;
        this.clearFieldErrors();
    }
    async loadTypes() {
        try {
            this.choice.receive(await api.fetchProfessionalTypes());
            /*
             * A profissao da ficha, restaurada DEPOIS de a lista chegar.
             *
             * O `show` restaura antes, e na primeira carga a lista ainda nao existe:
             * `restore` nao achava o tipo e a ficha mostrava "Selecione a profissao" -
             * e Salvar sem mexer gravava a ficha sem profissao (31/10/2026).
             */
            this.choice.restore(this.loaded?.professionalTypeId);
            this.showTypes();
        }
        catch (caught) {
            LOG.warn('Could not load the professional types', caught);
            // A lista e opcional: sem ela o resto da ficha continua editavel.
            this.scope.formError = 'Não foi possível carregar as profissões.';
        }
    }
    /**
     * A lista de profissoes, montada UMA vez.
     *
     * Escolher marca a opcao no lugar em vez de refazer a lista: o seletor le
     * `options` de forma reativa, e trocar o array durante o proprio `onChange` dele
     * realimentava a mudanca - "Maximum call stack size exceeded" com as 55 da base.
     */
    showTypes() {
        const escolhida = this.choice.chosenProfession;
        const especialidade = this.choice.chosenSpecialty;
        this.scope.types = this.opcoes(this.choice.professions, escolhida?.id, (id) => this.onChooseType(id));
        this.scope.typeSummary = escolhida?.name ?? '';
        this.scope.specialties = this.opcoes(this.choice.specialties, especialidade?.id, (id) => this.onChooseSpecialty(id));
        this.scope.specialtySummary = especialidade?.name ?? '';
        this.scope.regulated = this.choice.regulated;
    }
    /** Uma lista de opcoes do seletor, com a atual marcada. */
    opcoes(tipos, atual, escolher) {
        return tipos.map((type) => {
            const scope = new ProfessionalTypeScope();
            scope.identity = String(type.id);
            scope.label = type.name;
            scope.value = String(type.id);
            scope.current = atual === type.id;
            scope.onSelect = this.action(escolher.bind(this, type.id));
            scope.update = this.update;
            return scope;
        });
    }
    async loadCollaborators(professionalId) {
        this.scope.assignLoading = true;
        this.scope.assignError = undefined;
        try {
            this.collaborators = await api.fetchCollaborators(professionalId, this.companyId);
            this.showRows();
        }
        catch (caught) {
            LOG.warn('Could not load the collaborators', caught);
            this.scope.assignError = messageOf(caught, 'Não foi possível carregar os colaboradores.');
        }
        finally {
            this.scope.assignLoading = false;
        }
    }
    /**
     * O filtro e no CLIENTE, e de proposito.
     *
     * A lista e a dos colaboradores de UMA empresa, ja em maos - ir ao servidor a cada
     * tecla para reordenar o que ja esta na memoria seria trabalho por nada.
     */
    handleTermChanged(value) {
        this.scope.term = value;
        this.showRows();
    }
    showRows() {
        const term = this.scope.term.trim().toLowerCase();
        const digits = term.replace(/\D/g, '');
        const visiveis = this.collaborators.filter((row) => {
            if (!term)
                return true;
            if (row.name.toLowerCase().includes(term))
                return true;
            return digits.length > 0 && row.cpf.replace(/\D/g, '').includes(digits);
        });
        // As linhas que ja estao na tela sao atualizadas, e nao recriadas - ver `reuseRows`.
        this.scope.rows = reuseRows(this.scope.rows, visiveis, (row) => String(row.patientId), (row, reuse) => this.rowOf(row, reuse));
        // Os contadores olham a lista INTEIRA, e nao a filtrada: "3 de 40" muda de
        // significado se o 40 encolher quando alguem digita.
        this.scope.total = this.collaborators.length;
        this.scope.assignedCount = this.collaborators.filter((row) => row.assigned).length;
    }
    rowOf(row, reuse) {
        const scope = reuse ?? new AssignRowScope();
        scope.identity = String(row.patientId);
        scope.name = row.name;
        scope.cpf = row.cpf;
        scope.assigned = row.assigned;
        scope.busy = this.toggling.has(row.patientId);
        scope.onToggle = this.action(this.onToggle.bind(this, row.patientId));
        scope.update = this.update;
        return scope;
    }
    // ========== EDICAO ==========
    handleChanged(field, value) {
        this.scope[field] = value;
        this.touch();
    }
    handlePhoneChanged(value) {
        this.scope.phone = maskPhone(value);
        this.scope.phoneError = undefined;
        this.touch();
    }
    handleCpfChanged(value) {
        this.scope.cpf = maskCpf(value);
        this.scope.cpfError = undefined;
        this.touch();
    }
    /** `SC`, e nao `sc`: e como o legado grava, e como o resto do sistema le. */
    handleUfChanged(value) {
        this.scope.uf = value
            .toUpperCase()
            .replace(/[^A-Z]/g, '')
            .slice(0, 2);
        this.touch();
    }
    touch() {
        this.scope.dirty = true;
        this.scope.firstNameError = undefined;
        this.scope.lastNameError = undefined;
        if (this.scope.formError) {
            this.scope.formError = undefined;
        }
    }
    clearFieldErrors() {
        this.scope.firstNameError = undefined;
        this.scope.lastNameError = undefined;
        this.scope.phoneError = undefined;
        this.scope.cpfError = undefined;
    }
    /** Escolher a PROFISSAO derruba a especialidade - ela nao pertence a outra profissao. */
    async onChooseType(typeId) {
        this.choice.chooseProfession(typeId);
        this.showTypes();
        this.touch();
    }
    async onChooseSpecialty(specialtyId) {
        this.choice.chooseSpecialty(specialtyId);
        this.showTypes();
        this.touch();
    }
    async onClearSpecialty() {
        this.choice.clearSpecialty();
        this.showTypes();
        this.touch();
    }
    /** Desfaz a escolha, e leva o registro junto - ele so existia por causa dela. */
    async onClearType() {
        this.choice.clear();
        this.scope.registrationNumber = '';
        this.scope.uf = '';
        this.showTypes();
        this.touch();
    }
    async onSave() {
        if (!this.validate()) {
            return;
        }
        this.scope.saving = true;
        this.scope.formError = undefined;
        try {
            /*
             * O CPF PRIMEIRO, e sozinho: e o unico campo que o servidor pode recusar
             * por conflito. Por ultimo, uma recusa deixaria o resto ja gravado e a
             * tela mostrando erro sobre uma edicao que em parte aconteceu.
             */
            const cpf = this.scope.cpf.trim();
            if (cpf !== (this.loaded?.cpf ?? '')) {
                await api.updateCpf(this.userId, cpf, this.companyId);
            }
            await api.updateUser(this.userId, {
                firstName: this.scope.firstName.trim(),
                lastName: this.scope.lastName.trim(),
                socialName: this.scope.socialName.trim() || null,
                telephoneNumber: this.scope.phone.trim(),
                registrationNumber: this.scope.registrationNumber.trim() || null,
                uf: this.scope.uf.trim() || null,
                professionalTypeId: this.choice.professionalTypeId ?? null
            }, this.companyId);
            await this.load();
            await this.area?.refresh();
            this.app.notify('Dados salvos', 'Os dados deste usuário foram atualizados.', 'Continuar', 'account-edit');
        }
        catch (caught) {
            LOG.warn('Could not save the company user', caught);
            this.scope.formError = messageOf(caught, 'Não foi possível salvar.');
        }
        finally {
            this.scope.saving = false;
        }
    }
    validate() {
        this.clearFieldErrors();
        this.scope.formError = undefined;
        if (!this.scope.firstName.trim()) {
            this.scope.firstNameError = 'Informe o nome.';
        }
        if (!this.scope.lastName.trim()) {
            this.scope.lastNameError = 'Informe o sobrenome.';
        }
        if (!this.scope.phone.trim()) {
            this.scope.phoneError = 'Informe o telefone.';
        }
        if (!isValidCpf(this.scope.cpf)) {
            this.scope.cpfError = 'Informe um CPF válido.';
        }
        return !(this.scope.firstNameError || this.scope.lastNameError || this.scope.phoneError || this.scope.cpfError);
    }
    // ========== ACOES SOBRE A CONTA ==========
    async onToggle(patientId) {
        const row = this.collaborators.find((candidate) => candidate.patientId === patientId);
        if (!row) {
            return;
        }
        if (this.toggling.has(patientId)) {
            return;
        }
        const alvo = !row.assigned;
        this.toggling.add(patientId);
        this.showRows();
        let changed = false;
        try {
            await api.assign(this.loaded.professionalId, patientId, alvo, this.companyId);
            row.assigned = alvo;
            changed = true;
        }
        catch (caught) {
            LOG.warn('Could not change the assignment', caught);
            this.app.alert('error', 'Atribuição', messageOf(caught, 'Não foi possível alterar.'));
        }
        finally {
            // No sucesso e na falha: o interruptor volta a ouvir sem recarregar a pagina.
            this.toggling.delete(patientId);
            this.showRows();
        }
        // A contagem da lista atras muda so quando a atribuicao mudou.
        if (changed) {
            await this.area?.refresh();
        }
    }
    /**
     * Liga e desliga o ACESSO - e isso nao e desligar da empresa.
     *
     * Desativado continua no quadro e apenas nao entra; desligado sai do quadro e
     * perde os vinculos. Quem sai de licenca e o primeiro caso, e por isso as duas
     * acoes convivem na ficha.
     */
    async onToggleActive() {
        const alvo = !this.scope.active;
        if (!alvo) {
            this.app.confirm('Desativar acesso', `${this.scope.name} deixa de entrar no painel, mas continua no quadro da empresa. Continuar?`, 'Desativar', async () => {
                await this.action(this.postActive.bind(this, false))();
            }, { cancelText: 'Cancelar', icon: 'account-remove' });
            return;
        }
        await this.postActive(true);
    }
    async postActive(active) {
        this.scope.accountBusy = true;
        try {
            await api.setActive(this.userId, active, this.companyId);
            await this.load();
            await this.area?.refresh();
        }
        catch (caught) {
            LOG.warn('Could not change the account state', caught);
            this.app.alert('error', 'Acesso', messageOf(caught, 'Não foi possível alterar o acesso.'));
        }
        finally {
            this.scope.accountBusy = false;
        }
    }
    /**
     * Por que a ficha nao oferece desativar nem desligar.
     *
     * A propria conta e o caso de sempre - ninguem se tranca do lado de fora. Os outros
     * dois sao do B-30: o master so a plataforma alcanca, e os demais administradores so
     * o master.
     */
    reasonWithoutAccess(user) {
        if (user.podeAlterarAcesso) {
            return '';
        }
        if (String(user.userId) === String(this.app.currentSession?.id)) {
            return 'Esta é a sua conta: o acesso dela não se altera por aqui.';
        }
        if (user.master) {
            return 'O administrador master só pode ser desativado ou desligado pelo administrador da plataforma.';
        }
        return 'Só o administrador master da empresa desativa ou desliga administradores.';
    }
    /**
     * Passa a condicao de master a esta pessoa (B-30) - e PERGUNTA antes.
     *
     * Quem a passa sendo o master a perde no ato, e nao a recupera sozinho: so o novo
     * master, ou a plataforma, a devolve. E isso que a pergunta precisa dizer.
     */
    async onMakeMaster() {
        const session = this.app.currentSession;
        const plataforma = hasAnyRole(session?.roles ?? [], ['ROLE_ADMIN', 'ROLE_SUPER_ADMIN']);
        const message = plataforma
            ? `${this.scope.name} passa a ser o administrador master desta empresa, no lugar de quem é hoje. Continuar?`
            : `${this.scope.name} passa a ser o administrador master desta empresa. Você deixa de ser master no ato e não poderá mais cadastrar, desativar ou desligar administradores. Continuar?`;
        this.app.confirm('Tornar administrador master', message, 'Tornar master', async () => {
            await this.action(this.postMaster)();
        }, { cancelText: 'Cancelar', icon: 'account-edit' });
    }
    async postMaster() {
        this.scope.accountBusy = true;
        try {
            await api.transferMaster(this.userId, this.companyId);
            await this.load();
            await this.area?.refresh();
            this.app.notify('Administrador master', `${this.scope.name} agora é o administrador master.`, 'Continuar', 'user-check');
        }
        catch (caught) {
            LOG.warn('Could not transfer the master condition', caught);
            this.app.alert('error', 'Administrador master', messageOf(caught, 'Não foi possível transferir a condição de master.'));
        }
        finally {
            this.scope.accountBusy = false;
        }
    }
    async onRemove() {
        this.app.confirm('Desligar da empresa', `${this.scope.name} perde o acesso ao painel desta empresa. Continuar?`, 'Desligar', async () => {
            await this.action(this.postRemove)();
        }, { cancelText: 'Cancelar', icon: 'account-remove' });
    }
    async postRemove() {
        try {
            await api.removeUser(this.userId, this.companyId);
            await this.area?.refresh();
            // A ficha de quem saiu nao existe mais: volta para a lista.
            await CompanyUsersKeys.of(this.app).flip();
        }
        catch (caught) {
            LOG.warn('Could not remove the user', caught);
            this.app.alert('error', 'Desligar', messageOf(caught, 'Não foi possível desligar este usuário.'));
        }
    }
    async onBack() {
        await CompanyUsersKeys.of(this.app).flip();
    }
}
/** A mensagem do servidor, que costuma explicar melhor que um texto generico. */
function messageOf(caught, fallback) {
    return caught instanceof Error && caught.message ? caught.message : fallback;
}
//# sourceMappingURL=company-user-detail.presenter.js.map